import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Field, Logo } from "../components/Shared";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { LangSwitch } from "../components/LangSwitch";

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="flex items-center justify-between gap-3"><Logo /><LangSwitch /></div>
        <h1 className="mt-12 text-3xl font-bold text-slate-900">{title}</h1>
        <p className="mt-2 text-sm text-slate-500">{subtitle}</p>
        <div className="mt-8 max-w-md">{children}</div>
      </div>
      <div className="relative hidden lg:block">
        <img src="/images/boxes.jpg" alt="Cartons de déménagement" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b2c6b]/90 via-[#0b2c6b]/30 to-transparent" />
        <p className="absolute bottom-12 left-12 right-12 font-display text-3xl font-semibold text-white">Le logiciel intelligent des déménageurs.</p>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Connexion" subtitle="Accédez à l'espace de votre entreprise de déménagement.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="E-mail professionnel">
          <Input data-testid="login-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="vous@entreprise.fr" />
        </Field>
        <Field label="Mot de passe">
          <Input data-testid="login-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#0b2c6b] hover:bg-[#1559d6]" data-testid="login-submit-button">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Se connecter
        </Button>
        <p className="text-center text-sm text-slate-500">
          Pas encore de compte ? <Link to="/inscription" className="font-semibold text-[#1559d6]" data-testid="go-register-link">Créer un compte entreprise</Link>
        </p>
      </form>
    </AuthShell>
  );
}
